import type { Language } from "../lib/translations";
import { translations } from "../lib/translations";
import { Timeline } from "./Timeline";
import { communityTimeline } from "../lib/config";
import * as Lucide from "lucide-react";
import { Card, CardHeader, CardTitle } from "./ui/card";
import { ExpandableDescription } from "./ExpandableDescription";
import { useDynamicContent, getTimelineEvents } from "../lib/dynamicContent";
import { CommunitySkeleton } from "./ui/Skeletons";

interface CommunityTabProps {
  lang: Language;
}

const getCommunityIcon = (name: string) => {
  if (name && typeof name === "string" && Object.prototype.hasOwnProperty.call(Lucide, name)) {
    const IconComp = (Lucide as any)[name];
    if (typeof IconComp === "function" || (IconComp && typeof IconComp === "object")) {
      return IconComp;
    }
  }
  return Lucide.ShieldCheck;
};

export function CommunityTab({ lang }: CommunityTabProps) {
  const t = lang === "tr" ? translations.tr.community : translations.en.community;
  const { timeline, causes: dynamicCauses, loading } = useDynamicContent();

  if (loading) {
    return <CommunitySkeleton />;
  }

  const events = getTimelineEvents("community", timeline, communityTimeline);

  // Parse causes (dynamic with static fallback)
  let causesList = [];
  if (dynamicCauses && dynamicCauses.length > 0) {
    causesList = dynamicCauses.map((c) => ({
      title: lang === "tr" ? c.title_tr : c.title_en,
      desc: lang === "tr" ? c.desc_tr : c.desc_en,
      icon: getCommunityIcon(c.icon),
      color: c.color || "text-brand-primary bg-brand-primary/10 dark:bg-brand-primary/20"
    }));
  } else {
    // Fallback static causes
    causesList = [
      {
        title: t.causeEthicsTitle,
        desc: t.causeEthicsDesc,
        icon: Lucide.ShieldCheck,
        color: "text-brand-primary bg-brand-primary/10 dark:bg-brand-primary/20"
      },
      {
        title: t.causeOpenSourceTitle,
        desc: t.causeOpenSourceDesc,
        icon: Lucide.Code2,
        color: "text-brand-secondary bg-brand-secondary/10 dark:bg-brand-secondary/20"
      },
      {
        title: t.causeNextGenTitle,
        desc: t.causeNextGenDesc,
        icon: Lucide.Sparkles,
        color: "text-brand-accent bg-brand-accent/10 dark:bg-brand-accent/20"
      }
    ];
  }

  // Enforce max 4 causes cards limit
  const displayedCauses = causesList.slice(0, 4);

  return (
    <div className="space-y-12 max-w-7xl mx-auto py-6 animate-fade-in">
      {/* Introduction */}
      <section className="text-center md:text-left space-y-4">
        <h3 className="text-3xl font-extrabold tracking-tight text-brand-primary">
          {t.title}
        </h3>
        <p className="text-base text-foreground/80 leading-relaxed max-w-3xl">
          {t.intro}
        </p>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        {/* Left Side: Volunteering Timeline */}
        <section className="space-y-6">
          <h4 className="text-xl font-bold text-foreground border-b border-border/60 pb-3 flex items-center gap-2">
            <Lucide.Heart className="h-5 w-5 text-brand-accent fill-brand-accent/20" />
            {t.timelineTitle}
          </h4>
          <Timeline events={events} lang={lang} />
        </section>

        {/* Right Side: Causes & Values */}
        <section className="space-y-6">
          <h4 className="text-xl font-bold text-foreground border-b border-border/60 pb-3">
            {t.causesTitle}
          </h4>
          <div className="space-y-4">
            {displayedCauses.map((cause, idx) => {
              const Icon = cause.icon;
              return (
                <Card
                  key={idx}
                  className="border border-border/40 bg-card/45 backdrop-blur-md transition-all duration-300 hover:shadow-xl hover:shadow-brand-primary/5 hover:-translate-y-0.5 rounded-xl"
                >
                  <CardHeader className="flex flex-row items-start gap-4 p-5">
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/40 ${cause.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="text-left">
                      <CardTitle className="text-base font-bold text-foreground">
                        {cause.title}
                      </CardTitle>
                      <ExpandableDescription
                        text={cause.desc}
                        maxLines={3}
                        className="mt-1.5"
                      />
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

export default CommunityTab;
